import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { ScenarioSimulator } from "@/components/ScenarioSimulator";
import { ReadinessMilestones } from "@/components/ReadinessMilestones";
import { ShieldAlert, Activity, Clock, Banknote, FileDown } from "lucide-react";

const CRIT = {
  critical: "bg-red-50 text-red-700 border-red-200",
  important: "bg-amber-50 text-amber-700 border-amber-200",
};
const STATUS = {
  healthy: { dot: "bg-emerald-500", label: "Healthy" },
  at_risk: { dot: "bg-amber-500", label: "At risk" },
  impaired: { dot: "bg-red-500", label: "Impaired" },
};
const fmt = (v) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", notation: "compact", maximumFractionDigits: 1 }).format(v || 0);

function ServiceCard({ svc }) {
  const st = STATUS[svc.status] || STATUS.healthy;
  return (
    <div className="rounded-md border border-black/5 bg-white p-5" data-testid={`service-card-${svc.service_id}`}>
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-display text-sm font-semibold text-ek-ink">{svc.name}</h3>
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${CRIT[svc.criticality]}`}>
          {svc.criticality}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-xs text-ek-ink/65">
        <span className={`h-2 w-2 rounded-full ${st.dot}`} />
        {st.label}
        {svc.impaired_suppliers.length > 0 && (
          <span className="ml-1 inline-flex items-center gap-1 text-red-600">
            <ShieldAlert size={11} /> {svc.impaired_suppliers.length} supplier(s)
          </span>
        )}
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 border-t border-black/5 pt-3 text-center">
        <Stat icon={Clock} label="Tolerance" value={`${svc.impact_tolerance_hours}h`} />
        <Stat icon={Activity} label="RTO / RPO" value={`${svc.rto_hours} / ${svc.rpo_hours}h`} />
        <Stat icon={Banknote} label="Loss / day" value={fmt(svc.financial_impact_per_day)} />
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div>
    <Icon size={13} className="mx-auto text-ek-ink/30" />
    <p className="mt-1 text-xs font-semibold text-ek-ink">{value}</p>
    <p className="text-[10px] text-ek-ink/65">{label}</p>
  </div>
);

const Kpi = ({ label, value, tone }) => (
  <div className="rounded-md border border-black/5 bg-white p-4">
    <p className="text-xs font-medium uppercase tracking-wide text-ek-ink/65">{label}</p>
    <p className={`mt-1 font-display text-2xl font-bold ${tone || "text-ek-ink"}`}>{value}</p>
  </div>
);

export default function Resilience() {
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/resilience/summary").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const downloadSnapshot = async () => {
    setDownloading(true);
    try {
      const { data: blob } = await api.get("/dora/readiness-snapshot", { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ERIO-DORA-Readiness-Snapshot.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } finally {
      setDownloading(false);
    }
  };

  const s = data?.summary;

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Seo title="Operational Resilience" description="Critical business services, impact tolerances and the scenario simulator (DORA / ISO 22301)." path="/resilience" />
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-10" data-testid="resilience-page">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="ek-eyebrow">DORA · ISO 22301</h1>
            <h2 className="mt-1 font-display text-3xl font-bold text-ek-ink">Operational resilience</h2>
            <p className="mt-2 max-w-2xl text-sm text-ek-ink/65">
              Critical and important business services with impact tolerances — and a live simulator that quantifies what a disruption would really cost.
            </p>
          </div>
          <button
            type="button"
            onClick={downloadSnapshot}
            disabled={downloading}
            className="ek-btn inline-flex shrink-0 items-center gap-1.5 disabled:opacity-50"
            data-testid="dora-snapshot-download"
            title="One-page DORA readiness PDF — auto-current, generated from live data"
          >
            <FileDown size={15} />
            {downloading ? "Building…" : "DORA readiness snapshot (PDF)"}
          </button>
        </div>

        <div className="mt-6">
          <ScenarioSimulator />
          <a href="/scenario-studio" data-testid="resilience-studio-link"
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-ek-purple hover:underline">
            Open Scenario Studio for probabilistic (Monte-Carlo) loss curves &amp; saved scenarios →
          </a>
        </div>

        {s && (
          <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-5" data-testid="resilience-summary">
            <Kpi label="Services" value={s.total} />
            <Kpi label="Critical" value={s.critical} />
            <Kpi label="Important" value={s.important} />
            <Kpi label="At risk" value={s.at_risk} tone={s.at_risk ? "text-amber-600" : "text-ek-ink"} />
            <Kpi label="Impaired" value={s.impaired} tone={s.impaired ? "text-red-600" : "text-ek-ink"} />
          </div>
        )}

        <h3 className="mt-10 ek-eyebrow">Critical business services</h3>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {data?.services.map((svc) => (
            <ServiceCard key={svc.service_id} svc={svc} />
          ))}
        </div>

        <ReadinessMilestones />
      </main>
    </div>
  );
}
